import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Loader2, MessageSquare, Radio, Settings2, Tag } from 'lucide-react'
import {
  SocialTrackerItem,
  SocialTrackerProposal,
} from '@digital-twins/shared-types'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'
import { knowledgeThreadsApi, socialListeningApi } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { SocialPlatformIcon } from '../KnowledgeHubPage/social/SocialPlatformIcon'
import { SocialTrackerDialog } from '../KnowledgeHubPage/social/SocialTrackerDialog'
import {
  askAboutTrackerHref,
  SOCIAL_PLATFORM_LABELS,
} from '../KnowledgeHubPage/social/socialFormat'
import {
  isTrackerInProgress,
  STATUS_TONE_BADGE_VARIANT,
  trackerStatusTone,
} from '../KnowledgeHubPage/social/socialTrackerStatus'
import { useSocialTrackers } from '../KnowledgeHubPage/social/useSocialTrackers'

interface ThreadSocialTrackerCardProps {
  proposal: SocialTrackerProposal
  threadToken: string
  messageToken: string
  /** The question the agent answered; "Ask again" re-asks it on the new posts. */
  question: string
  onProposalChange: (proposal: SocialTrackerProposal) => void
}

function toggle(values: Array<string>, value: string): Array<string> {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

interface ChipProps {
  label: string
  isSelected: boolean
  isDisabled: boolean
  testId: string
  onToggle: () => void
}

function Chip(props: ChipProps) {
  const { label, isSelected, isDisabled, testId, onToggle } = props
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      disabled={isDisabled}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[13px] transition-colors disabled:cursor-default',
        isSelected
          ? 'border-primary/40 bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground line-through hover:text-foreground',
      )}
      onClick={onToggle}
    >
      {isSelected ? <Check className="h-3 w-3" /> : null}
      {label}
    </button>
  )
}

/**
 * The tracker the Ask agent suggested. Nothing runs until the client clicks
 * Create; after that the card follows the tracker's sync through the shared
 * tracker list (which polls while it is queued, collecting or indexing).
 */
export function ThreadSocialTrackerCard(props: ThreadSocialTrackerCardProps) {
  const { proposal, threadToken, messageToken, question, onProposalChange } = props

  const { t } = useTranslation('app')
  const { isEnabled, canManage, trackers, replaceTracker } = useSocialTrackers(true)

  const [keywords, setKeywords] = useState(proposal.keywords)
  const [handles, setHandles] = useState(proposal.handles)
  const [labelKeys, setLabelKeys] = useState(
    proposal.labels.map((label) => `${label.kind}:${label.value}`),
  )
  const [isCreating, setIsCreating] = useState(false)
  const [isCustomizing, setIsCustomizing] = useState(false)

  const createdTracker: SocialTrackerItem | null = proposal.createdTrackerToken
    ? (trackers.find((tracker) => tracker.token === proposal.createdTrackerToken) ?? null)
    : null
  const isCreated = proposal.createdTrackerToken != null
  const draft = useMemo<SocialTrackerProposal>(
    () => ({
      ...proposal,
      keywords,
      handles,
      labels: proposal.labels.filter((label) =>
        labelKeys.includes(`${label.kind}:${label.value}`),
      ),
      keywordNotes: (proposal.keywordNotes ?? []).filter((note) =>
        keywords.includes(note.keyword),
      ),
    }),
    [proposal, keywords, handles, labelKeys],
  )

  if (!isEnabled) {
    return null
  }

  async function markCreated(tracker: SocialTrackerItem) {
    replaceTracker(tracker)
    try {
      const updated = await knowledgeThreadsApi.markSocialTrackerProposalCreated(
        threadToken,
        messageToken,
        tracker.token,
      )
      onProposalChange(updated)
    } catch {
      onProposalChange({ ...draft, createdTrackerToken: tracker.token })
    }
  }

  async function handleCreate() {
    setIsCreating(true)
    try {
      const tracker = await socialListeningApi.createTracker({
        name: draft.name,
        platform: draft.platform,
        keywords: draft.keywords,
        ...(draft.handles.length > 0 ? { handles: draft.handles } : {}),
        language: draft.language,
        market: draft.market,
        labels: draft.labels,
        ...(draft.brief ? { brief: draft.brief } : {}),
        keywordNotes: draft.keywordNotes ?? [],
        topics: draft.topics ?? [],
      })
      toast.success(t('socialListening.toast.created'))
      await markCreated(tracker)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('socialListening.form.saveFailed')
      toast.error(message)
    } finally {
      setIsCreating(false)
    }
  }

  function renderStatus() {
    if (!createdTracker) {
      return (
        <Text as="p" size="sm" color="muted" data-testid="thread-social-tracker-status">
          {t('socialListening.proposal.starting')}
        </Text>
      )
    }
    const isInProgress = isTrackerInProgress(createdTracker.status)
    const isReady = createdTracker.status === 'ready' || createdTracker.status === 'partial'
    const askHref = isReady
      ? askAboutTrackerHref(createdTracker, { question: question || undefined })
      : null
    return (
      <div className="space-y-3" data-testid="thread-social-tracker-status">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={STATUS_TONE_BADGE_VARIANT[trackerStatusTone(createdTracker.status)]}>
            {isInProgress ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            {t(`socialListening.status.${createdTracker.status}`)}
          </Badge>
          {isReady ? (
            <Text as="span" size="sm" color="muted">
              {t('socialListening.detail.postCount', {
                count: createdTracker.totalPosts,
                value: createdTracker.totalPosts,
              })}
            </Text>
          ) : null}
        </div>
        <Text as="p" size="sm" color="muted">
          {isInProgress
            ? t('socialListening.proposal.syncing')
            : isReady
              ? t('socialListening.proposal.ready')
              : t('socialListening.proposal.notReady')}
        </Text>
        <div className="flex flex-wrap gap-2">
          {askHref ? (
            <Button asChild variant="cta" shape="pill" size="sm">
              <Link href={askHref} data-testid="thread-social-tracker-ask-again">
                <MessageSquare className="h-4 w-4" />
                {t('socialListening.proposal.askAgain')}
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" shape="pill" size="sm">
            <Link
              href={`${ROUTES.knowledge}?view=social&tracker=${createdTracker.token}`}
              data-testid="thread-social-tracker-open"
            >
              {t('socialListening.proposal.openTracker')}
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <section
      className="mt-3 space-y-3 rounded-2xl border border-border bg-card p-4"
      data-testid="thread-social-tracker-card"
    >
      <header className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <SocialPlatformIcon platform={proposal.platform} />
        </span>
        <div className="min-w-0">
          <Text as="p" size="xs" color="muted" className="flex items-center gap-1">
            <Radio className="h-3 w-3" />
            {t('socialListening.proposal.eyebrow', {
              platform: SOCIAL_PLATFORM_LABELS[proposal.platform],
            })}
          </Text>
          <Text as="p" weight="medium" data-testid="thread-social-tracker-name">
            {proposal.name}
          </Text>
          {proposal.rationale ? (
            <Text as="p" size="sm" color="muted">
              {proposal.rationale}
            </Text>
          ) : null}
        </div>
      </header>

      <div className="space-y-2">
        <Text as="p" size="xs" color="muted">
          {t('socialListening.proposal.searchTerms')}
        </Text>
        <div className="flex flex-wrap gap-1.5">
          {proposal.keywords.map((keyword) => (
            <Chip
              key={`k-${keyword}`}
              label={keyword}
              isSelected={keywords.includes(keyword)}
              isDisabled={isCreated || !canManage}
              testId={`thread-social-tracker-keyword-${keyword}`}
              onToggle={() => setKeywords((prev) => toggle(prev, keyword))}
            />
          ))}
          {proposal.handles.map((handle) => (
            <Chip
              key={`h-${handle}`}
              label={`@${handle}`}
              isSelected={handles.includes(handle)}
              isDisabled={isCreated || !canManage}
              testId={`thread-social-tracker-handle-${handle}`}
              onToggle={() => setHandles((prev) => toggle(prev, handle))}
            />
          ))}
        </div>
        {proposal.labels.length > 0 ? (
          <>
            <Text as="p" size="xs" color="muted" className="flex items-center gap-1">
              <Tag className="h-3 w-3" />
              {t('socialListening.form.labels')}
            </Text>
            <div className="flex flex-wrap gap-1.5">
              {proposal.labels.map((label) => {
                const key = `${label.kind}:${label.value}`
                return (
                  <Chip
                    key={key}
                    label={`${t(`socialListening.form.labelKind.${label.kind}`)}: ${label.value}`}
                    isSelected={labelKeys.includes(key)}
                    isDisabled={isCreated || !canManage}
                    testId={`thread-social-tracker-label-${key}`}
                    onToggle={() => setLabelKeys((prev) => toggle(prev, key))}
                  />
                )
              })}
            </div>
          </>
        ) : null}
        {proposal.brief?.include || proposal.brief?.exclude ? (
          <div className="space-y-0.5" data-testid="thread-social-tracker-brief">
            {proposal.brief.include ? (
              <Text as="p" size="xs">
                <span className="text-muted-foreground">{t('socialListening.form.briefInclude')}: </span>
                {proposal.brief.include}
              </Text>
            ) : null}
            {proposal.brief.exclude ? (
              <Text as="p" size="xs">
                <span className="text-muted-foreground">{t('socialListening.form.briefExclude')}: </span>
                {proposal.brief.exclude}
              </Text>
            ) : null}
          </div>
        ) : null}
        {(proposal.topics ?? []).length > 0 ? (
          <Text as="p" size="xs" data-testid="thread-social-tracker-topics">
            <span className="text-muted-foreground">{t('socialListening.form.topics')}: </span>
            {(proposal.topics ?? []).map((topic) => topic.name).join(', ')}
          </Text>
        ) : null}
        <Text as="p" size="xs" color="muted">
          {t('socialListening.proposal.market', {
            market: proposal.market,
            language: proposal.language,
          })}
        </Text>
      </div>

      {isCreated ? (
        renderStatus()
      ) : canManage ? (
        <div className="space-y-2">
          <Text as="p" size="xs" color="muted">
            {t('socialListening.proposal.costNote')}
          </Text>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="cta"
              shape="pill"
              size="sm"
              disabled={isCreating || keywords.length === 0}
              data-testid="thread-social-tracker-create"
              onClick={() => void handleCreate()}
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('socialListening.proposal.create')}
            </Button>
            <Button
              type="button"
              variant="outline"
              shape="pill"
              size="sm"
              disabled={isCreating}
              data-testid="thread-social-tracker-customize"
              onClick={() => setIsCustomizing(true)}
            >
              <Settings2 className="h-4 w-4" />
              {t('socialListening.proposal.customize')}
            </Button>
          </div>
        </div>
      ) : (
        <Text as="p" size="xs" color="muted">
          {t('socialListening.proposal.needsAdmin')}
        </Text>
      )}

      <SocialTrackerDialog
        open={isCustomizing}
        tracker={null}
        initialProposal={draft}
        onOpenChange={setIsCustomizing}
        onSaved={(tracker) => void markCreated(tracker)}
      />
    </section>
  )
}
